import React, { useState } from "react";
import {
    Button,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    IconButton,
    MenuItem,
} from "@mui/material";
import AddBox from "@mui/icons-material/AddBox";
import CloseIcon from "@mui/icons-material/Close";
import Grid from "@mui/material/Grid2";
import validator from "validator";
import axios from "axios";
import { useSnackbar } from "notistack";
import { useTranslation } from "react-i18next";
import { defaultVariables } from "../../../../config";
import LoadingButton from "../../../components/elements/LoadingButton";
import RayaField from "../../../components/elements/fields/RayaField";
import MobileField from "../../../components/elements/fields/MobileField";
import AppCardLoaderFields from "../../../components/loaders/AppCardLoaderFields";
import { Pencil } from "lucide-react";

const MakeMarketingRepButton = ({ reload, item_id, edit, iconOnly }) => {
    const [open, setOpen] = useState(false);
    const { t } = useTranslation();
    const { enqueueSnackbar } = useSnackbar();
    const [loading, setLoading] = useState(false);
    const [submitLoading, setSubmitLoading] = useState(false);
    const [errors, setErrors] = useState({});
    const [fields, setFields] = useState({
        first_name: "",
        last_name: "",
        mobile: "",
        country: null,
        region: "",
    });

    const loginToken = localStorage.getItem("ACT");
    const config = {
        headers: {
            Accept: "application/json",
            Authorization: `Bearer ${loginToken}`,
        },
    };

    const resetFields = () => {
        setFields({
            first_name: "",
            last_name: "",
            mobile: "",
            country: null,
            region: "",
        });
        setErrors({});
    };

    const loadData = async () => {
        setLoading(true);
        try {
            const response = await axios.get(`${defaultVariables.url}/marketing-representatives/get/${item_id}`, config);
            setFields({
                first_name: response.data.first_name,
                last_name: response.data.last_name,
                mobile: response.data.mobile?.startsWith(response.data.code)
                    ? response.data.mobile.slice(String(response.data.code).length)
                    : (response.data.mobile || ""),
                country: { phonecode: response.data.code || "90" },
                region: response.data.region || "",
            });
        } catch (error) {
            enqueueSnackbar(t("detailsSetFailed"), { variant: "error" });
        } finally {
            setLoading(false);
        }
    };

    const handleOpen = async () => {
        resetFields();
        setOpen(true);
        if (item_id) {
            await loadData();
        }
    };

    const handleChange = (e) => {
        const { name, value } = e.target;
        setFields((prev) => ({ ...prev, [name]: value }));
        setErrors((prev) => ({ ...prev, [name]: "" }));
    };

    const setMobile = (country, mobile) => {
        setFields((prev) => ({ ...prev, country, mobile }));
        setErrors((prev) => ({ ...prev, mobile: "" }));
    };

    const validate = () => {
        const newErrors = {};
        let valid = true;

        if (!fields.first_name.trim()) {
            valid = false;
            newErrors.first_name = t("emptyField");
        }
        if (!fields.last_name.trim()) {
            valid = false;
            newErrors.last_name = t("emptyField");
        }
        if (!validator.isNumeric(fields.mobile) || fields.mobile.length < 9) {
            valid = false;
            newErrors.mobile = t("wrongMobile");
        }

        setErrors(newErrors);
        return valid;
    };

    const handleSubmit = async () => {
        if (!validate()) return;

        setSubmitLoading(true);
        const data = new FormData();
        if (item_id) data.append("id", item_id);
        data.append("first_name", fields.first_name);
        data.append("last_name", fields.last_name);
        let mobile = Number(fields.mobile).toString();
        mobile = fields.country?.phonecode + mobile;
        data.append("mobile", mobile);
        data.append("code", fields.country?.phonecode || "90");
        data.append("region", fields.region);

        try {
            await axios.post(`${defaultVariables.url}/marketing-representatives/make`, data, config);
            enqueueSnackbar(t("detailsSet"), { variant: "success" });
            setOpen(false);
            reload && reload();
        } catch (error) {
            if (error.response?.status === 403) {
                enqueueSnackbar(t("existMobile"), { variant: "error" });
            } else {
                enqueueSnackbar(t("detailsSetFailed"), { variant: "error" });
            }
        } finally {
            setSubmitLoading(false);
        }
    };

    return (
        <>
            {edit ? (
                <Button variant="outlined" color="secondary" size="small" style={{ margin: 5 }} onClick={handleOpen} endIcon={<Pencil size={16} />}>
                    {t("edit")}
                </Button>
            ) : iconOnly ? (
                <IconButton color="primary" onClick={handleOpen}>
                    <AddBox />
                </IconButton>
            ) : (
                <Button variant="contained" color="secondary" startIcon={<AddBox />} onClick={handleOpen}>
                    {t("marketingAddRep")}
                </Button>
            )}

            <Dialog open={open} onClose={() => setOpen(false)} maxWidth="sm" fullWidth>
                <DialogTitle>
                    {item_id ? t("marketingEditRep") : t("marketingAddRep")}
                    <IconButton onClick={() => setOpen(false)} style={{ position: "absolute", right: 8, top: 8 }}>
                        <CloseIcon />
                    </IconButton>
                </DialogTitle>
                <DialogContent dividers>
                    {loading ? (
                        <AppCardLoaderFields />
                    ) : (
                        <Grid container spacing={2}>
                            <Grid item size={{ xs: 12, md: 6 }}>
                                <RayaField
                                    required
                                    fullWidth
                                    variant="outlined"
                                    name="first_name"
                                    label={t("first_name")}
                                    value={fields.first_name}
                                    onChange={handleChange}
                                    error={!!errors.first_name}
                                    helperText={errors.first_name}
                                />
                            </Grid>
                            <Grid item size={{ xs: 12, md: 6 }}>
                                <RayaField
                                    required
                                    fullWidth
                                    variant="outlined"
                                    name="last_name"
                                    label={t("last_name")}
                                    value={fields.last_name}
                                    onChange={handleChange}
                                    error={!!errors.last_name}
                                    helperText={errors.last_name}
                                />
                            </Grid>
                            <Grid item size={12}>
                                <MobileField setMobile={setMobile} error={errors.mobile} />
                            </Grid>
                            <Grid item size={12}>
                                <RayaField
                                    fullWidth
                                    variant="outlined"
                                    name="region"
                                    label={t("marketingRegion")}
                                    placeholder={t("marketingRegionPlaceholder")}
                                    value={fields.region}
                                    onChange={handleChange}
                                />
                            </Grid>
                        </Grid>
                    )}
                </DialogContent>
                <DialogActions>
                    <LoadingButton variant="contained" color="secondary" loading={submitLoading} onClick={handleSubmit}>
                        {t("setDetails")}
                    </LoadingButton>
                </DialogActions>
            </Dialog>
        </>
    );
};

export default MakeMarketingRepButton;
